import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import { getMomentTz } from "@dexteel/mesf-core";
import { ExpandMore } from "@mui/icons-material";
import {
	Box,
	Collapse,
	Grid2,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useCalendarAreaId } from "../../context/CalendarAreaContext";
import { useSearchCampaignsByEventId } from "../../hooks/useSearchEvents";
import { CampaignEventData } from "../../models/CampaignEventData";

const moment = getMomentTz();

const fmtInt = (v: number | null | undefined) =>
	v != null
		? Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(v)
		: "";
const fmtLen = (v: number | null | undefined) =>
	v != null
		? Intl.NumberFormat("en-US", {
				minimumFractionDigits: 1,
				maximumFractionDigits: 1,
			}).format(v)
		: "";
const fmtPct = (v: number | null | undefined) =>
	v != null ? `${fmtInt(v)}%` : "";

// Columns after the first "Heat Number" column.
const COLUMNS: {
	key: keyof CampaignEventData;
	label: string;
	fmt: (v: unknown) => string;
}[] = [
	{ key: "Grade", label: "Grade", fmt: (v) => (v as string) ?? "" },
	{
		key: "BilletLength",
		label: "Billet Length",
		fmt: (v) => (v as string) ?? "",
	},
	{
		key: "HotbedLength",
		label: "Hotbed Length",
		fmt: (v) => fmtLen(v as number),
	},
	{
		key: "ShearLength",
		label: "Shear Length",
		fmt: (v) => fmtLen(v as number),
	},
	{
		key: "BilletsCharged",
		label: "Billets Charged",
		fmt: (v) => fmtInt(v as number),
	},
	{
		key: "BilletsRolled",
		label: "Billets Rolled",
		fmt: (v) => fmtInt(v as number),
	},
	{
		key: "TonsCharged",
		label: "Tons Charged",
		fmt: (v) => fmtInt(v as number),
	},
	{ key: "TonsRolled", label: "Tons Rolled", fmt: (v) => fmtInt(v as number) },
	{
		key: "TonsFinished",
		label: "Tons Finished",
		fmt: (v) => fmtInt(v as number),
	},
	{
		key: "BilletsRejected",
		label: "Billets Rejected",
		fmt: (v) => fmtInt(v as number),
	},
	{ key: "Cobbles", label: "Cobbles", fmt: (v) => fmtInt(v as number) },
	{ key: "MillYield", label: "Mill Yield", fmt: (v) => fmtPct(v as number) },
	{
		key: "FinishedYield",
		label: "Finished Yield",
		fmt: (v) => fmtPct(v as number),
	},
];
// Columns that only carry per-heat detail (blank at the month-subtotal level):
// hidden from the collapsed month rows, but kept in the expandable detail table.
const SUMMARY_HIDDEN = new Set<keyof CampaignEventData>([
	"Grade",
	"BilletLength",
	"HotbedLength",
	"ShearLength",
]);
const SUMMARY_COLUMNS = COLUMNS.filter((c) => !SUMMARY_HIDDEN.has(c.key));
// Month rows lead with 4 columns (Month, First Billet Timestamp, Heat Count, Roll
// Change) matching the detail's leading 4 (Heat Number, Date, Grade, Billet Length).
const SUMMARY_TOTAL_COLS = SUMMARY_COLUMNS.length + 4;

// Length columns not populated for now: also hidden from the detail table.
const DETAIL_HIDDEN = new Set<keyof CampaignEventData>([
	"HotbedLength",
	"ShearLength",
]);
const DETAIL_COLUMNS = COLUMNS.filter((c) => !DETAIL_HIDDEN.has(c.key));

// M1 does not track charged / cobbles / rejected (every tracked billet is rolled),
// so these columns are left blank for M1 instead of showing 0 / 100% yields.
const M1_HIDDEN = new Set<keyof CampaignEventData>([
	"BilletsCharged",
	"TonsCharged",
	"BilletsRejected",
	"Cobbles",
	"MillYield",
	"FinishedYield",
]);
// Cell text for a data column: blank when the column is hidden for M1.
const cellText = (
	c: (typeof COLUMNS)[number],
	row: CampaignEventData,
	isM1: boolean,
) => (isM1 && M1_HIDDEN.has(c.key) ? "" : c.fmt(row[c.key]));

const SUM_COLS = [
	"BilletsCharged",
	"BilletsRolled",
	"TonsCharged",
	"TonsRolled",
	"TonsFinished",
	"BilletsRejected",
	"Cobbles",
] as const;

const isRollChange = (r: CampaignEventData) =>
	r.HeatNumber?.startsWith("Roll change") ?? false;

// Hours portion of the roll-change label ("Roll change - 4.93 hrs" -> "4.93 hrs").
// A month without a roll change (e.g. the second month) shows a dash.
const rollChangeHours = (r: CampaignEventData | null) =>
	r?.HeatNumber?.replace(/^Roll change\s*-\s*/i, "") ?? "-";

// Compact timestamp for the "First Billet Timestamp" / "Date" columns (mill local time).
const fmtTs = (d: Date | null | undefined) =>
	d ? moment(d).format("M/D HH:mm") : "";

// Per-month subtotal, computed client-side from the month's detail rows (ticket #2258).
const computeSubtotal = (rows: CampaignEventData[]): CampaignEventData => {
	const sum = (k: (typeof SUM_COLS)[number]) =>
		rows.reduce((acc, r) => acc + (r[k] ?? 0), 0);
	const tonsCharged = sum("TonsCharged");
	const tonsRolled = sum("TonsRolled");
	const tonsFinished = sum("TonsFinished");
	return {
		HeatNumber: null,
		Grade: null,
		BilletLength: null,
		HotbedLength: null,
		ShearLength: null,
		BilletsCharged: sum("BilletsCharged"),
		BilletsRolled: sum("BilletsRolled"),
		TonsCharged: tonsCharged,
		TonsRolled: tonsRolled,
		TonsFinished: tonsFinished,
		BilletsRejected: sum("BilletsRejected"),
		Cobbles: sum("Cobbles"),
		MillYield:
			tonsCharged > 0 ? Math.round((tonsRolled / tonsCharged) * 100) : null,
		FinishedYield:
			tonsCharged > 0 ? Math.round((tonsFinished / tonsCharged) * 100) : null,
		CampaignStart: null,
		MonthStart: null,
		RowOrder: 0,
		IsTotal: 1,
	};
};

type MonthGroup = {
	key: string;
	label: string;
	firstBillet: Date | null;
	rows: CampaignEventData[];
	subtotal: CampaignEventData;
};

// Color pattern mirrored from Sales Overview (see sales/components/utils.ts):
// subtle row borders, bold gray headers for the main table, uppercase #888
// headers for the detail subtable.
const cellSx = {
	fontSize: "0.8rem",
	py: 0.5,
	whiteSpace: "nowrap" as const,
	borderBottom: "1px solid #f0f0f0",
};
const headerCellSx = {
	...cellSx,
	fontWeight: 600,
	color: "#555",
	borderBottom: "1px solid #ddd",
};
const subCellSx = {
	fontSize: "0.72rem",
	py: 0.25,
	color: "#555",
	whiteSpace: "nowrap" as const,
	borderBottom: "1px solid #f0f0f0",
};
const subHeaderCellSx = {
	...subCellSx,
	fontWeight: 600,
	color: "#888",
	borderBottom: "1px solid #eee",
};

// Shared column widths so the collapsed month table and the detail table line up
// (both render with table-layout: fixed). 13 columns: label + date + 11 data.
const COL_WIDTHS = ["10%", "13%", ...Array<string>(11).fill("7%")];

// Detail-table widths: the subtable is indented (ml), so its first column (Heat)
// is narrowed and the slack put on the last column, shifting the middle columns
// left toward the month header. Tune the first value to move detail columns.
const DETAIL_COL_WIDTHS = ["5%", "13%", ...Array<string>(11).fill("7%")];

// One expandable month row: the per-month subtotal, expanding to that month's detail table.
const MonthSection = ({
	group,
	rollChange,
	isM1,
}: {
	group: MonthGroup;
	rollChange: CampaignEventData | null;
	isM1: boolean;
}) => {
	const [open, setOpen] = useState(false);
	return (
		<>
			<TableRow
				onClick={() => setOpen((v) => !v)}
				sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#fafafa" } }}
			>
				<TableCell sx={cellSx}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<ExpandMore
							fontSize="small"
							sx={{
								fontSize: "1rem",
								color: "#777",
								transform: open ? "rotate(180deg)" : "rotate(0deg)",
								transition: "transform 0.2s",
							}}
						/>
						{group.label}
					</Box>
				</TableCell>
				<TableCell sx={cellSx} align="right">
					{fmtTs(group.firstBillet)}
				</TableCell>
				<TableCell sx={cellSx} align="right">
					{fmtInt(group.rows.length)}
				</TableCell>
				<TableCell sx={cellSx} align="right">
					{rollChangeHours(rollChange)}
				</TableCell>
				{SUMMARY_COLUMNS.map((c) => (
					<TableCell key={c.key} sx={cellSx} align="right">
						{cellText(c, group.subtotal, isM1)}
					</TableCell>
				))}
			</TableRow>
			<TableRow>
				<TableCell colSpan={SUMMARY_TOTAL_COLS} sx={{ p: 0, border: 0 }}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<Box
							sx={{
								backgroundColor: "#fafafa",
								pl: 8,
								pr: 0,
								py: 1,
								isolation: "isolate",
							}}
						>
							<Box sx={{ maxHeight: 220, overflowY: "auto" }}>
								<Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
									<TableHead>
										<TableRow>
											<TableCell
												sx={{
													...subHeaderCellSx,
													backgroundColor: "#fff",
													width: DETAIL_COL_WIDTHS[0],
													zIndex: 3,
												}}
											>
												Heat
											</TableCell>
											<TableCell
												sx={{
													...subHeaderCellSx,
													backgroundColor: "#fff",
													width: DETAIL_COL_WIDTHS[1],
													zIndex: 3,
												}}
												align="right"
											>
												Start
											</TableCell>
											{DETAIL_COLUMNS.map((c, i) => (
												<TableCell
													key={c.key}
													sx={{
														...subHeaderCellSx,
														width: DETAIL_COL_WIDTHS[i + 2],
														zIndex: 3,
														backgroundColor: "#fff",
													}}
													align="right"
												>
													{c.label}
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{group.rows.map((r, i) => (
											<TableRow key={`${r.HeatNumber}-${i}`}>
												<TableCell sx={subCellSx}>{r.HeatNumber}</TableCell>
												<TableCell sx={subCellSx} align="right">
													{fmtTs(r.CampaignStart)}
												</TableCell>
												{DETAIL_COLUMNS.map((c) => (
													<TableCell key={c.key} sx={subCellSx} align="right">
														{cellText(c, r, isM1)}
													</TableCell>
												))}
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

export const CampaignDataTable = ({
	selectedEventId,
	setError,
}: {
	selectedEventId: number;
	setError: (error: string) => void;
}) => {
	const {
		data,
		error: e,
		isError,
		isLoading,
	} = useSearchCampaignsByEventId({
		eventId: selectedEventId,
		isDraftMode: false,
	});

	const { rollChangeRow, months } = useMemo(() => {
		const rows = data?.campaigns ?? [];
		let rollChange: CampaignEventData | null = null;
		const byMonth = new Map<string, CampaignEventData[]>();
		for (const r of rows) {
			if (isRollChange(r)) {
				rollChange = r;
				continue;
			}
			if (r.IsTotal === 1) continue;
			const key = r.MonthStart
				? moment.utc(r.MonthStart).format("YYYY-MM")
				: "—";
			if (!byMonth.has(key)) byMonth.set(key, []);
			byMonth.get(key)!.push(r);
		}
		const groups: MonthGroup[] = [...byMonth.keys()].sort().map((key) => {
			const detail = byMonth.get(key)!;
			const ms = detail[0]?.MonthStart ?? null;
			const firstBillet = detail.reduce<Date | null>(
				(min, r) =>
					r.CampaignStart && (!min || r.CampaignStart < min)
						? r.CampaignStart
						: min,
				null,
			);
			return {
				key,
				label: ms ? moment.utc(ms).format("MMMM YYYY") : "—",
				firstBillet,
				rows: detail,
				subtotal: computeSubtotal(detail),
			};
		});
		return { rollChangeRow: rollChange, months: groups };
	}, [data]);

	useEffect(() => {
		if (e && isError) setError(e.message);
	}, [e, isError]);

	const isM1 = Number(useCalendarAreaId()) === MILL_1.id;

	return (
		<Grid2 size={{ md: 12 }} component={Paper} sx={{ p: 2 }}>
			<Typography variant="h6" fontWeight={600} fontSize="1rem" sx={{ mb: 1 }}>
				Campaign Info
			</Typography>
			{isLoading ? (
				<CenteredLazyLoading minHeight={50} />
			) : months.length === 0 ? (
				<Typography variant="caption" color="text.secondary">
					No campaign info
				</Typography>
			) : (
				<Box>
					<Table size="small" sx={{ tableLayout: "fixed" }}>
						<TableHead>
							<TableRow>
								<TableCell sx={{ ...headerCellSx, width: COL_WIDTHS[0] }}>
									Month
								</TableCell>
								<TableCell
									sx={{ ...headerCellSx, width: COL_WIDTHS[1] }}
									align="right"
								>
									First Billet Timestamp
								</TableCell>
								<TableCell
									sx={{ ...headerCellSx, width: COL_WIDTHS[2] }}
									align="right"
								>
									Heat Count
								</TableCell>
								<TableCell
									sx={{ ...headerCellSx, width: COL_WIDTHS[3] }}
									align="right"
								>
									Roll Change
								</TableCell>
								{SUMMARY_COLUMNS.map((c, i) => (
									<TableCell
										key={c.key}
										sx={{ ...headerCellSx, width: COL_WIDTHS[i + 4] }}
										align="right"
									>
										{c.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						{months.map((g, idx) => (
							<TableBody key={g.key}>
								<MonthSection
									group={g}
									rollChange={idx === 0 ? rollChangeRow : null}
									isM1={isM1}
								/>
							</TableBody>
						))}
					</Table>
				</Box>
			)}
		</Grid2>
	);
};
